<template>
  <model-form
    ref="modelForm"
    :model="membershipVoucher"
    :title="`${membershipVoucher.membershipTypeId ? `Edit ${membershipVoucher.name} for ${membershipType.name}` : `Add Voucher to ${membershipType.name}`}`"
    :submodel="true"
    modal-type="modal"
  >
    <div class="column">
      <div class="row q-col-gutter-x-sm items-center q-mb-md">
        <q-input
          v-model="membershipVoucher.name"
          class="col-9"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-toggle
          v-model="membershipVoucher.enable"
          class="col-3 q-pb-md"
          label="Enable"
          bottom-slots
        >
          <q-tooltip>
            Disable this voucher to prevent future assignment to or unlimited redemption by members.
          </q-tooltip>
        </q-toggle>
        <q-select
          v-model="membershipVoucher.voucherType"
          class="col-12"
          label="Voucher Type"
          :options="voucherTypes.data"
          option-label="name"
          emit-value
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          @update:model-value="input => { if (!membershipVoucher.name) { membershipVoucher.name = input.name; } }"
        >
          <q-tooltip>
            The voucher to be assigned.
          </q-tooltip>
        </q-select>
      </div>
      <div
        class="row q-col-gutter-x-sm items-center"
      >
        <q-toggle
          v-model="membershipVoucher.limitRedemptions"
          class="col-12 q-pb-md"
          label="Assign Voucher / Limit Redemptions"
          bottom-slots
        >
          <q-tooltip>
            Limit how many ticket vouchers a member can redeem within a given period.
            <br>Unlimited vouchers are restricted to a single member.
          </q-tooltip>
        </q-toggle>
        <q-input
          v-model.number="membershipVoucher.redemptionsLimit"
          class="col-6"
          label="Redemptions Limit"
          type="number"
          step="1"
          min="1"
          :disable="!membershipVoucher.limitRedemptions"
          :rules="[val => !!val && val > 0 || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            The number of vouchers assigned to the member at the beginning of each of their membership terms.
          </q-tooltip>
        </q-input>
        <q-select
          v-model="membershipVoucher.grantType"
          class="col-6"
          label="Grant To"
          :options="$mh.asOptions(MEMBERSHIP_VOUCHER_GRANT_TYPES.ALL)"
          emit-value
          map-options
          :disable="!membershipVoucher.limitRedemptions"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip style="max-width: 400px;">
                      {{ MEMBERSHIP_VOUCHER_GRANT_TYPES_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Grant vouchers to individual members of this type, to each members (from the membership owner) once, or to all members from any applicable member.
          </q-tooltip>
        </q-select>
        <q-input
          v-model.number="membershipVoucher.renews"
          :disable="!membershipVoucher.limitRedemptions || $_.without(MEMBERSHIP_VOUCHER_CONDITIONS.DATES, ...$mh.objectToArray(membershipVoucher.conditions)).length === MEMBERSHIP_VOUCHER_CONDITIONS.DATES.length"
          class="col-6"
          label="Renew Every"
          type="number"
          step="1"
          min="1"
          :max="maximumRenews"
          :rules="[val => !!val && val > 0 && val <= maximumRenews || ('Field cannot exceed ' + maximumRenews)]"
          bottom-slots
        >
          <q-tooltip>
            How often vouchers are assigned, multiplied by the given term.
            <br>Vouchers that are not date conditional (e.g. birthdays) are assigned in bulk on membership renewal.
            <br>Annual vouchers are assigned to annual members only.
          </q-tooltip>
        </q-input>
        <q-select
          v-model="membershipVoucher.renewalTerm"
          class="col-6"
          label="Renewal Term"
          :options="$mh.asOptions(MEMBERSHIP_VOUCHER_RENEWAL_TERMS.ALL)"
          emit-value
          map-options
          :disable="!membershipVoucher.limitRedemptions"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        >
          <q-tooltip>
            The unit of time used for assigning member vouchers.
          </q-tooltip>
        </q-select>
        <!-- COMMENTED OUT AS THIS FUNCTIONALITY WAS NOT / HAS NEVER BEEN IMPLEMENTED -->
        <!-- <q-checkbox
          v-if="!membershipVoucher.limitRedemptions"
          v-model="membershipVoucher.forbidRewatchingMovies"
          class="col-6 q-pb-md"
          label="Forbid Rewatching Movies"
          bottom-slots
        />
        <div v-if="!membershipVoucher.limitRedemptions" class="col-6" />
        <q-checkbox
          v-if="!membershipVoucher.limitRedemptions"
          v-model="membershipVoucher.forbidMultiplePerDay"
          class="col-6 q-pb-lg"
          label="Forbid Multiple Per Day"
          bottom-slots
        /> -->
        <model-form-select
          field="conditions"
          class="col-12 q-mt-md"
          label="Conditions"
          :options="$mh.asOptions(MEMBERSHIP_VOUCHER_CONDITIONS.ALL)"
          map-options
          emit-value
          multiple
          use-chips
          clearable
          :decode="conditions => $mh.objectToArray(conditions)"
          :encode="enabledConditions => $mh.arrayToObject(enabledConditions || [], { allKeys: MEMBERSHIP_VOUCHER_CONDITIONS.ALL })"
        >
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip>
                      {{ MEMBERSHIP_VOUCHER_CONDITIONS_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Set conditions to be met for members to receive this voucher. If multiple conditions are selected, all must be met for voucher assignment.
          </q-tooltip>
        </model-form-select>
        <!-- Condition Requirements -->
        <date-range-condition-form
          v-if="membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.DATE_RANGE]"
          :conditional="membershipVoucher"
          :date-range-start-field="CONDITION_REQUIREMENTS.DATE_RANGE_START"
          :date-range-end-field="CONDITION_REQUIREMENTS.DATE_RANGE_END"
          :exclude-date-range-field="CONDITION_REQUIREMENTS.EXCLUDE_DATE_RANGE"
        />

        <day-of-week-condition-form
          v-if="membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.DAY_OF_WEEK]"
          :conditional="membershipVoucher"
          :day-of-week-field="CONDITION_REQUIREMENTS.DAY_OF_WEEK"
          :exclude-day-of-week-field="CONDITION_REQUIREMENTS.EXCLUDE_DAY_OF_WEEK"
        />

        <longevity-condition-form
          v-if="membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.LONGEVITY]"
          :conditional="membershipVoucher"
        />

        <sites-condition-form
          v-if="membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.SITES]"
          :conditional="membershipVoucher"
        />

        <tier-condition-form
          v-if="membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER] || membershipVoucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER_UPGRADE]"
          :conditional="membershipVoucher"
          :membership-tiers="membershipType.membershipTiers.data"
        />
      </div>
    </div>
  </model-form>
</template>

<script>
import MembershipType from 'src/models/membership-type/MembershipType'
import { CONDITION_REQUIREMENTS, MEMBERSHIP_VOUCHER_CONDITIONS, MEMBERSHIP_VOUCHER_GRANT_TYPES, MEMBERSHIP_VOUCHER_RENEWAL_TERMS } from 'src/models/membership-type/MembershipVoucher'
import DateRangeConditionForm from 'src/modules/admin/components/condition-requirement-forms/DateRangeConditionForm'
import DayOfWeekConditionForm from 'src/modules/admin/components/condition-requirement-forms/DayOfWeekConditionForm'
import LongevityConditionForm from 'src/modules/admin/components/condition-requirement-forms/LongevityConditionForm'
import SitesConditionForm from 'src/modules/admin/components/condition-requirement-forms/SitesConditionForm'
import TierConditionForm from 'src/modules/admin/components/condition-requirement-forms/TierConditionForm'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const MEMBERSHIP_VOUCHER_CONDITIONS_TOOLTIPS = {
  tier: 'Vouchers will be assigned to active members of the given tier.',
  'day-of-week': 'Vouchers will be assigned to active members on the given day of the week.',
  birthday: 'Vouchers will be assigned to active members on their birthday.',
  'date-range': 'Vouchers will be assigned to active members during a configurable date range.',
  longevity: 'Vouchers will be assigned to active members who have maintained their membership for the given interval.',
  sites: 'Vouchers will be redeemable on orders for the given sites.',
  'tier-upgrade': 'Vouchers will be assigned to active members when upgrading to the given tier.'
}

const MEMBERSHIP_VOUCHER_GRANT_TYPES_TOOLTIPS = {
  [MEMBERSHIP_VOUCHER_GRANT_TYPES.OWNER]: 'Vouchers will only be assigned to the membership owner, if the owner has this membership type. No other members will be granted vouchers.',
  [MEMBERSHIP_VOUCHER_GRANT_TYPES.INDIVIDUAL]: 'Vouchers will be assigned to each individual member with this membership type.',
  [MEMBERSHIP_VOUCHER_GRANT_TYPES.EACH_MEMBER_ONCE]: 'Vouchers will be assigned once to every member on the membership, as long as one member has this membership type. Additional members of this type will not cause additional vouchers to be assigned.',
  [MEMBERSHIP_VOUCHER_GRANT_TYPES.ALL_MEMBERS]: 'Vouchers will be assigned to all members on the membership for every member with this membership type.'
}

export default {
  name: 'MembershipVoucherForm',
  components: {
    DateRangeConditionForm,
    DayOfWeekConditionForm,
    LongevityConditionForm,
    ModelForm,
    SitesConditionForm,
    TierConditionForm
  },
  configuration: ['voucherTypes'],
  props: {
    membershipType: {
      type: MembershipType,
      required: true
    }
  },
  models: {
    membershipVoucher: {
      membershipType: 'default',
      voucherType: 'default',
      membershipTierIds: 'default'
    }
  },
  computed: {
    maximumRenews () {
      return this.membershipVoucher.renewalTerm === MEMBERSHIP_VOUCHER_RENEWAL_TERMS.YEARS ? 5 : (5 * 12)
    }
  },
  watch: {
    'membershipVoucher.conditions': {
      handler () {
        if (_.without(MEMBERSHIP_VOUCHER_CONDITIONS.DATES, ...this.$mh.objectToArray(this.membershipVoucher.conditions)).length === MEMBERSHIP_VOUCHER_CONDITIONS.DATES.length) {
          this.membershipVoucher.renews = 1
        }
      },
      deep: true
    }
  },
  created () {
    _.extend(this, {
      CONDITION_REQUIREMENTS,
      MEMBERSHIP_VOUCHER_CONDITIONS,
      MEMBERSHIP_VOUCHER_CONDITIONS_TOOLTIPS,
      MEMBERSHIP_VOUCHER_GRANT_TYPES,
      MEMBERSHIP_VOUCHER_GRANT_TYPES_TOOLTIPS,
      MEMBERSHIP_VOUCHER_RENEWAL_TERMS
    })
  }
}

</script>
